// Geolocation utilities — extracted from MyScripts/Utils.ts

import { hasValue } from './validation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { renderImperativeModal, ImperativeModalHandle } from './imperative-modal';

// Lazy accessors — these window globals are set by other modules that may not
// have evaluated yet when this module first loads (circular import chains).
const w = window as any;
function isUWPApp() { return w.isUWPApp; }
function isIOSApp() { return w.isIOSApp; }
function isAndroidApp() { return w.isAndroidApp; }
function isWindowsAppSdkApp() { return w.isWindowsAppSdkApp; }
function notifyApp(action: string, args: any[]) { return w.notifyApp(action, args); }
function getJSErrorType() { return w.JSErrorType; }
function getKSTPositionError() { return w.KSTPositionError; }

export const GEO_IP_BASED_MODE_KEY = 'orna_geo_ip_based_mode';
export const GEO_CACHED_POSITION_KEY = 'orna_geo_cached_position';
export const GEO_ACCURACY_THRESHOLD_IP_BASED = 1000;
export const GEO_ACCURACY_THRESHOLD_HIGH_ACCURACY = 100;
export const GEO_IP_BASED_CACHE_MAX_AGE = 3600000;

let locationCallbackFuncNum: number = 0;
let locationCallbackObj: Record<string, any> = {};

export function locationCallbackSuccess(callbackId: string, position: any): void {
  if (hasValue(locationCallbackObj[callbackId])) {
    locationCallbackObj[callbackId].successCallback(position);
    locationCallbackObj[callbackId] = null;
  }
}

export function locationCallbackFail(callbackId: string, error: GeolocationPositionError = null): void {
  if (hasValue(locationCallbackObj[callbackId])) {
    locationCallbackObj[callbackId].failCallback(error);
    locationCallbackObj[callbackId] = null;
  }
}

export function isIpBasedGeolocationMode(): boolean {
  try {
    return localStorage.getItem(GEO_IP_BASED_MODE_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

export function setIpBasedGeolocationMode(isIpBased: boolean): void {
  try {
    if (isIpBased) {
      localStorage.setItem(GEO_IP_BASED_MODE_KEY, 'true');
    } else {
      localStorage.removeItem(GEO_IP_BASED_MODE_KEY);
      localStorage.removeItem(GEO_CACHED_POSITION_KEY);
    }
  } catch (e) {
    // localStorage not available
  }
}

export function getCachedGeolocationPosition(): any | null {
  try {
    const cached = localStorage.getItem(GEO_CACHED_POSITION_KEY);
    if (!cached) return null;

    const data = JSON.parse(cached);
    const age = Date.now() - data.timestamp;

    if (age > GEO_IP_BASED_CACHE_MAX_AGE) {
      localStorage.removeItem(GEO_CACHED_POSITION_KEY);
      return null;
    }

    return data;
  } catch (e) {
    return null;
  }
}

export function setCachedGeolocationPosition(position: GeolocationPosition): void {
  try {
    const toCache = {
      coords: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed,
      },
      timestamp: Date.now(),
    };
    localStorage.setItem(GEO_CACHED_POSITION_KEY, JSON.stringify(toCache));
  } catch (e) {
    // localStorage not available or quota exceeded
  }
}

export function getGeolocationOptions(useIpBasedMode: boolean = false): PositionOptions {
  if (useIpBasedMode) {
    return {
      enableHighAccuracy: false,
      timeout: 60000,
      maximumAge: GEO_IP_BASED_CACHE_MAX_AGE,
    };
  }
  return {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
  };
}

export function getGeolocation(
  successCallback: PositionCallback,
  failCallback: PositionErrorCallback,
  options: PositionOptions = null,
): void {
  locationCallbackFuncNum += 1;
  let callbackId: string = `a${locationCallbackFuncNum}`;
  locationCallbackObj[callbackId] = { successCallback: successCallback, failCallback: failCallback };

  if (isUWPApp() || isIOSApp() || isAndroidApp() || isWindowsAppSdkApp()) {
    notifyApp("locationCallback", [callbackId]);
    return;
  }

  if (isIpBasedGeolocationMode()) {
    const cachedPosition = getCachedGeolocationPosition();
    if (cachedPosition) {
      locationCallbackSuccess(callbackId, cachedPosition);
      return;
    }
  }

  if (!options) {
    options = getGeolocationOptions(isIpBasedGeolocationMode());
  }
  if (options.timeout === Infinity) {
    options.timeout = 10000;
  }
  if (!hasValue(App.geoLocation)) {
    App.geoLocation = navigator.geolocation;
  }
  if (hasValue(App.geoLocation)) {
    setTimeout(() => {
      const positionError: GeolocationPositionError = new (getKSTPositionError())({ code: new (getKSTPositionError())({}).PERMISSION_DENIED, message: "Orna detected geolocation callback error." });
      locationCallbackFail(callbackId, positionError);
    }, options.timeout + 2000);
    App.geoLocation.getCurrentPosition(
      (position: GeolocationPosition): void => {
        if (position.coords.accuracy <= GEO_ACCURACY_THRESHOLD_HIGH_ACCURACY) {
          setIpBasedGeolocationMode(false);
        } else if (position.coords.accuracy > GEO_ACCURACY_THRESHOLD_IP_BASED) {
          setIpBasedGeolocationMode(true);
          setCachedGeolocationPosition(position);
        }
        locationCallbackSuccess(callbackId, position);
      },
      (error: GeolocationPositionError): void => {
        if (options.enableHighAccuracy && error?.code !== 1) {
          const initialErrorMsg = `Geolocation high-accuracy failed (code: ${error?.code}, message: ${error?.message}). Retrying with IP-based settings.`;
          api.insertNewJSError(initialErrorMsg, null, null, getJSErrorType().CAUGHT, null);

          const ipBasedOptions = getGeolocationOptions(true);
          App.geoLocation.getCurrentPosition(
            (position: GeolocationPosition): void => {
              if (position.coords.accuracy <= GEO_ACCURACY_THRESHOLD_HIGH_ACCURACY) {
                setIpBasedGeolocationMode(false);
              } else if (position.coords.accuracy > GEO_ACCURACY_THRESHOLD_IP_BASED) {
                setIpBasedGeolocationMode(true);
                setCachedGeolocationPosition(position);
              }
              locationCallbackSuccess(callbackId, position);
            },
            (retryError: GeolocationPositionError): void => {
              const fallbackErrorMsg = `Geolocation IP-based fallback also failed (code: ${retryError?.code}, message: ${retryError?.message}).`;
              api.insertNewJSError(fallbackErrorMsg, null, null, getJSErrorType().CAUGHT, null);
              locationCallbackFail(callbackId, retryError);
            },
            ipBasedOptions,
          );
        } else {
          locationCallbackFail(callbackId, error);
        }
      },
      options,
    );
  } else {
    const positionError: GeolocationPositionError = new (getKSTPositionError())({ code: new (getKSTPositionError())({}).PERMISSION_DENIED, message: "navigator.geolocation unavailable" });
    locationCallbackFail(callbackId, positionError);
  }
}

// Geolocation settings modal helpers (Windows App SDK)

export let _geolocationSettingsHandle: ImperativeModalHandle | null = null;
export let _geolocationSettingsState: { isOpen: boolean, bodyHtml: string, confirmDisabled: boolean } | null = null;

//Called directly by windows app. Do not change method signature without reviewing the app code.
export function windowsAppSdkAppGeolocationSettingsSuccess(): void {
  if (_geolocationSettingsState) {
    _geolocationSettingsState.confirmDisabled = false;
    _renderGeolocationSettingsModal();
  }
}

//Called directly by windows app. Do not change method signature without reviewing the app code.
export function windowsAppSdkAppGeolocationSettingsError(errorMessage: string): void {
  const bodyHtml = "<p>" + errorMessage + " <a href='ms-settings:privacy-location'>Click here and enable Location service.</a></p>";

  if (_geolocationSettingsState) {
    _geolocationSettingsState.confirmDisabled = true;
    _geolocationSettingsState.bodyHtml = bodyHtml;
    _geolocationSettingsState.isOpen = true;
    _renderGeolocationSettingsModal();
    return;
  }

  _geolocationSettingsState = {
    isOpen: true,
    bodyHtml: bodyHtml,
    confirmDisabled: true
  };

  _renderGeolocationSettingsModal();
}

export function _renderGeolocationSettingsModal(): void {
  if (!_geolocationSettingsState) return;

  const props = {
    isOpen: _geolocationSettingsState.isOpen,
    bodyHtml: _geolocationSettingsState.bodyHtml,
    confirmDisabled: _geolocationSettingsState.confirmDisabled,
    onConfirm: function () {
      if (_geolocationSettingsState) {
        _geolocationSettingsState.isOpen = false;
        _renderGeolocationSettingsModal();
      }
    }
  };

  if (_geolocationSettingsHandle) {
    _geolocationSettingsHandle.update(props);
  } else {
    _geolocationSettingsHandle = renderImperativeModal(
      (window as any).GeolocationSettingsModalReact,
      props
    );
  }
}
